import type { FC, KeyboardEvent, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { Label } from '@/components/ui/label'

/** Stops Enter in a single-line field from submitting a long form by accident. */
export const preventEnterSubmit = (event: KeyboardEvent<HTMLFormElement>): void => {
    const target = event.target as HTMLElement

    if (event.key === 'Enter' && target.tagName === 'INPUT' && (target as HTMLInputElement).type !== 'submit') event.preventDefault()
}

interface FormFieldProps {
    label: ReactNode
    htmlFor?: string
    required?: boolean
    hint?: ReactNode
    error?: string | null
    children: ReactNode
    className?: string
    labelAction?: ReactNode
}

export const FormField: FC<FormFieldProps> = ({
    label,
    htmlFor,
    required,
    hint,
    error,
    children,
    className,
    labelAction
}): ReactNode => {
    return (
        <div className={cn('grid min-w-0 content-start gap-1.5', className)}>
            <div className='flex min-h-5 items-center justify-between gap-2'>
                <Label htmlFor={htmlFor} className='text-[0.8rem] font-medium text-foreground'>
                    {label}

                    {required && (
                        <span className='-ml-1 text-rose-600' aria-hidden='true'>
                            *
                        </span>
                    )}
                </Label>

                {labelAction}
            </div>

            {children}

            {hint && !error && (
                <p className='text-xs leading-relaxed text-muted-foreground'>
                    {hint}
                </p>
            )}

            {error && (
                <p role='alert' className='text-xs font-medium text-destructive'>
                    {error}
                </p>
            )}
        </div>
    )
}

interface ChipButtonProps {
    active?: boolean
    onClick: () => void
    children: ReactNode
    className?: string
    title?: string
}

export const ChipButton: FC<ChipButtonProps> = ({ active, onClick, children, className, title }): ReactNode => {
    return (
        <button
            type='button'
            title={title}
            aria-pressed={active}
            onClick={onClick}
            className={cn(
                'inline-flex h-7 items-center gap-1 rounded-full border px-2.5 text-xs font-medium whitespace-nowrap transition-colors outline-none focus-visible:ring-3 focus-visible:ring-ring/50',
                active
                    ? 'border-primary/40 bg-accent text-accent-foreground'
                    : 'border-border bg-white text-muted-foreground hover:border-primary/30 hover:text-foreground',
                className
            )}
        >
            {children}
        </button>
    )
}

interface CheckRowProps {
    id: string
    checked: boolean
    onCheckedChange: (checked: boolean) => void
    label: ReactNode
    description?: ReactNode
    className?: string
}

/** A large, easy-to-tap checkbox row with a label and optional description. */
export const CheckRow: FC<CheckRowProps> = ({ id, checked, onCheckedChange, label, description, className }): ReactNode => {
    return (
        <label
            htmlFor={id}
            className={cn(
                'flex cursor-pointer items-start gap-3 rounded-xl border bg-white p-3 transition-colors hover:bg-muted/40 has-[:focus-visible]:ring-3 has-[:focus-visible]:ring-ring/50',
                checked && 'border-primary/35 bg-accent/50',
                className
            )}
        >
            <input
                id={id}
                type='checkbox'
                checked={checked}
                onChange={event => onCheckedChange(event.target.checked)}
                className='mt-0.5 size-4 shrink-0 cursor-pointer rounded accent-[var(--primary)]'
            />

            <span className='min-w-0'>
                <span className='block text-sm font-medium text-foreground'>
                    {label}
                </span>

                {description && (
                    <span className='mt-0.5 block text-xs leading-relaxed text-muted-foreground'>
                        {description}
                    </span>
                )}
            </span>
        </label>
    )
}
